import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, User, CreditCard, CornerDownLeft } from "lucide-react";
import { api } from "@/lib/api";

export default function CommandPalette({ open, setOpen }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setOpen]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    else { setQ(""); setRes(null); }
  }, [open]);

  const run = useCallback(async (query) => {
    if (query.trim().length < 2) { setRes(null); return; }
    setBusy(true);
    try { const { data } = await api.get(`/admin/search?q=${encodeURIComponent(query)}`); setRes(data); }
    catch { setRes(null); } finally { setBusy(false); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => run(q), 250);
    return () => clearTimeout(t);
  }, [q, run]);

  const go = (link) => { setOpen(false); nav(link); };

  if (!open) return null;
  const users = res?.users || [];
  const payments = res?.payments || [];
  const empty = q.trim().length >= 2 && !busy && users.length === 0 && payments.length === 0;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[12vh] px-4" data-testid="command-palette">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} />
      <div className="relative w-full max-w-xl bg-[#1F0B10] border border-[#781826]/60 rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3.5 border-b border-[#781826]/40">
          <Search className="w-5 h-5 text-[#C59B27]" />
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)}
            data-testid="command-palette-input"
            placeholder="Search users by name / Profile ID / mobile / email, or a payment…"
            className="flex-1 bg-transparent outline-none text-[#FAF5F0] text-sm placeholder:text-[#8A6D71]" />
          <kbd className="text-[10px] text-[#8A6D71] border border-[#781826]/50 rounded px-1.5 py-0.5">ESC</kbd>
        </div>
        <div className="max-h-[52vh] overflow-y-auto p-2">
          {q.trim().length < 2 && <p className="text-xs text-[#8A6D71] px-3 py-6 text-center">Type at least 2 characters. Tip: press <span className="text-[#C59B27]">⌘K</span> anywhere to open this.</p>}
          {busy && <p className="text-xs text-[#8A6D71] px-3 py-4">Searching…</p>}
          {empty && <p className="text-xs text-[#8A6D71] px-3 py-6 text-center">No matches for “{q}”.</p>}
          {users.length > 0 && <div className="px-2 pt-1 pb-1 text-[10px] uppercase tracking-wide text-[#C59B27]">Users</div>}
          {users.map((u) => (
            <button key={u.id} onClick={() => go(u.link)} data-testid={`palette-user-${u.id}`}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#2A0F14] text-left group">
              <User className="w-4 h-4 text-[#D8B4B9]" />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[#FAF5F0] truncate">{u.name}</div>
                <div className="text-[11px] text-[#8A6D71] font-mono">{u.code || u.id?.slice(0, 8)}{u.city ? ` · ${u.city}` : ""}</div>
              </div>
              <CornerDownLeft className="w-3.5 h-3.5 text-[#8A6D71] opacity-0 group-hover:opacity-100" />
            </button>
          ))}
          {payments.length > 0 && <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-wide text-[#C59B27]">Payments</div>}
          {payments.map((p, i) => (
            <button key={i} onClick={() => go(p.link)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-[#2A0F14] text-left">
              <CreditCard className="w-4 h-4 text-[#D8B4B9]" />
              <div className="flex-1 min-w-0"><div className="text-sm text-[#FAF5F0] truncate">{p.text}</div><div className="text-[11px] text-[#8A6D71] font-mono">{p.ref || ""}</div></div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
